import React, { useState, useEffect } from 'react'
import LanguageStrings from '../../../../i18n/locales'
import { Grid, Button, CircularProgress } from '@mui/material'
import { styled } from '@mui/system'
import SearchIcon from '../../../../assets/SearchIcon.png'
import ParametersTable from './ParametersTable'
import CustomToast from '../ToastComponent'
import { connect, type ConnectedProps } from 'react-redux'
import { saveDefaultParamConfig } from '../../redux/actionCreators'
import { type DefaultParameterState } from '../../redux/addDefaultParameterSlice'
import { type RootState } from '../../../../store'
import Modal from '../../../../components/Modal'
import { APIConfiguration } from '../../../../utils/apiConfiguration'
import './ModbusParameters.css'
import { type Parameter } from '../interface'

const httpStatus = APIConfiguration
const AddDeviceStrings =
  LanguageStrings().DeviceManagementCollectionStrings.AddDeviceComponent

const HContainer = styled('div')({
  marginTop: '5px',
  marginBottom: '5px'
})
const Heading5 = styled('h5')({
  color: '#F0F0F0',
  fontFamily: 'Honeywell Sans Web',
  fontSize: '20px',
  fontStyle: 'normal',
  fontWeight: 800,
  lineHeight: '30px'
})
const Span = styled('span')({
  color: '#B0B0B0',
  textAlign: 'right',
  fontFeatureSettings: 'clig off, liga off',
  fontFamily: 'Honeywell Sans Web',
  fontStyle: 'normal',
  fontSize: '16px'
})
const BContainer = styled('div')({
  width: '1400px',
  height: '505px',
  padding: '24px 0px',
  gap: '16px',
  flexShrink: 0,
  borderRadius: '10px',
  border: '1px solid var(--Greyscale-Grey-n7-101010, #101010)',
  background: 'var(--background-dark-component-container-1, #272727)',
  color: '#F0F0F0'
})
const Input = styled('input')({
  width: '248px',
  height: '35px',
  padding: '8px 8px 8px 40px',
  borderRadius: '5px',
  border: '1px solid #ccc',
  backgroundImage: `url(${SearchIcon})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: '8px center',
  backgroundColor: '#3d3d3d',
  '::placeholder': {
    color: 'var(--typography-dark-secondary, #D0D0D0)',
    fontFeatureSettings: 'clig off, liga off',
    fontFamily: '"Honeywell Sans Web"',
    fontSize: '14px',
    fontStyle: 'italic',
    fontWeight: 500,
    lineHeight: '20px'
  }
})

interface ParentProps {
  assetId: string
  assetTypeId: string
  parameters: Parameter[] | []
  setParameters: (parameterList: Parameter[]) => void
  setIsModbusError: React.Dispatch<React.SetStateAction<any>>
}

const ModbusParameters = (props: PropsFromRedux & ParentProps): JSX.Element => {
  const [searchText, setSearchText] = useState<string>('')
  const [defaultViewSaved, setDefaultViewSaved] = useState<boolean>(false)
  const [selectAll, setSelectAll] = useState<boolean>(false)
  const [isDefaultParamSaved, setIsDefaultParamSaved] = React.useState(false)
  const [hasRegisterEnabledParameter, setHasRegisterEnabledParameter] =
    useState<boolean>(false)

  useEffect(() => {
    const hasEnabled = props.parameters.some(
      (parameter) => parameter.isRegisterEnabled
    )
    setHasRegisterEnabledParameter(hasEnabled)
  }, [props.parameters])

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSearchText(event.target.value)
  }

  const toggleSelectAll = (): void => {
    const newSelectAll = !selectAll
    setSelectAll(newSelectAll)
    const updateModbusParameters = props.parameters.map((modbusParameter) => ({
      ...modbusParameter,
      isRegisterEnabled: newSelectAll,
      ...(newSelectAll
        ? {
            pollingIntervalMinutes: modbusParameter.pollingIntervalMinutes ?? 60
          }
        : {
            // Reset values when disabling all, but keep default 1 minute
            pollingIntervalMinutes: 60,
            isAlarmEnabled: false
          })
    }))
    setDefaultViewSaved(false)
    props.setParameters(updateModbusParameters)
  }

  const toggleSelectIndicator = (registerAddress: number): void => {
    const updateModbusParameters = props.parameters.map(
      (modbusParameter: Parameter) =>
        modbusParameter.registerAddress === registerAddress
          ? {
              ...modbusParameter,
              isRegisterEnabled: !modbusParameter.isRegisterEnabled
            }
          : modbusParameter
    )
    const allSelected = updateModbusParameters.every(
      (modbusParameters: any) => modbusParameters.isRegisterEnabled
    )
    setSelectAll(allSelected)
    const parametersToUpdate = updateModbusParameters.find(
      (modbusParameter: Parameter) =>
        modbusParameter.registerAddress === registerAddress
    )
    if (parametersToUpdate != null) {
      if (!parametersToUpdate.isRegisterEnabled) {
        const resetValues = {
          pollingIntervalMinutes: 60, // Keep default 1 minute when disabled
          isAlarmEnabled: false
        }

        Object.assign(parametersToUpdate, resetValues)
      } else {
        // Set default values when enabling a parameter
        if (parametersToUpdate.pollingIntervalMinutes == null || parametersToUpdate.pollingIntervalMinutes === '') {
          parametersToUpdate.pollingIntervalMinutes = 60 // Default to 1 minute
        }
      }
    }
    setDefaultViewSaved(false)
    props.setParameters(updateModbusParameters)
  }

  const handleChangeTextBox = (registerAddress: number | string, propertyName: string, value: string | number): void => {
    let parsedValue: number | string = value
    if (propertyName === 'alarmLowerLimit' || propertyName === 'alarmHigherLimit' || propertyName === 'pollingIntervalMinutes') {
      parsedValue = parseInt(value as string)
      const minValue = propertyName === 'pollingIntervalMinutes' ? 10 : -2147483647
      const maxValue = propertyName === 'pollingIntervalMinutes' ? 525600 : 2147483647
      props.setIsModbusError(true)
      if (parsedValue < minValue || parsedValue > maxValue) {
        CustomToast({
          type: 'error',
          heading: AddDeviceStrings.Error,
          message: `Value must be between ${minValue} and ${maxValue}`
        })
        return
      }
    }
    props.setIsModbusError(false)
    const updateModbusParameters = props.parameters.map((modbusParameter) =>
      modbusParameter.registerAddress === registerAddress
        ? { ...modbusParameter, [propertyName]: parsedValue }
        : modbusParameter
    )
    setDefaultViewSaved(false)
    props.setParameters(updateModbusParameters)
  }

  const handleAlarmSelect = (
    registerAddress: number | string,
    propertyName: string,
    value: string
  ): void => {
    const updateModbusParameters = props.parameters.map((modbusParameter) =>
      modbusParameter.registerAddress === registerAddress
        ? { ...modbusParameter, [propertyName]: value }
        : modbusParameter
    )
    setDefaultViewSaved(false)
    props.setParameters(updateModbusParameters)
  }

  const handleCancel = (id: string | number): void => {
    const emptyObj = {
      isHigherLimitAlarmEnabled: false,
      alarmHigherLimit: null,
      higherLimitOperator: null,
      alarmHigherLimitLabelId: null,
      alarmHigherLimitRecommendationText: '',
      alarmHigherLimitSeverityId: null,
      alarmHigherLimitStateText: '',
      alarmHigherLimitTextColorId: null,
      isLowerLimitAlarmEnabled: false,
      alarmLowerLimit: null,
      lowerLimitOperator: null,
      alarmLowerLimitLabelId: null,
      alarmLowerLimitRecommendationText: '',
      alarmLowerLimitSeverityId: null,
      alarmLowerLimitStateText: null,
      alarmLowerLimitTextColorId: null
    }

    const updateModbusParameters = props.parameters.map((modbusParameter) =>
      modbusParameter.registerAddress === id
        ? { ...modbusParameter, ...emptyObj }
        : modbusParameter
    )
    if (updateModbusParameters !== null) props.setParameters(updateModbusParameters)
  }

  const saveDefaultParamConfig = (): void => {
    setDefaultViewSaved(true)
    props.saveDefaultParamConfig(props.parameters, props.assetTypeId)
    setIsDefaultParamSaved(true)
  }
  // istanbul ignore next
  const handleDefaultParamWithFailure = (): JSX.Element | null => {
    if (
      !props.defaultParameterState.isLoading &&
      props.defaultParameterState.httpStatus === httpStatus.CREATED_HTTP_STATUS
    ) {
      CustomToast({
        type: 'success',
        heading: AddDeviceStrings.DefaultViewSaved,
        message: AddDeviceStrings.ModbusParametersAddedSuccessfully
      })
      setIsDefaultParamSaved(false)
      return null
    } else if (props.defaultParameterState.isLoading) {
      return (
        <Modal title={AddDeviceStrings.SaveDefaultView}>
          <div className="CircularprogressAmi">
            <CircularProgress />
          </div>
        </Modal>
      )
    } else if (
      !props.defaultParameterState.isLoading &&
      props.defaultParameterState?.error !== ''
    ) {
      CustomToast({
        type: 'error',
        heading: AddDeviceStrings.SaveDefaultViewError,
        message: props.defaultParameterState?.error
      })
      setIsDefaultParamSaved(false)
      return null
    }
    return null
  }

  const tableHeaders = [
    AddDeviceStrings.Registers,
    AddDeviceStrings.ParameterName,
    AddDeviceStrings.ExecutionFrequency,
    AddDeviceStrings.Alarm,
    ''
  ]
  const searchParam =
    searchText !== ''
      ? props.parameters.filter(
        (parameter) =>
          parameter.parameterName
            ?.toLowerCase()
            .includes(searchText.toLowerCase()) ||
          parameter.registerAddress?.toString().includes(searchText)
      )
      : props.parameters
  const selectedModbusCount =
    searchParam != null
      ? searchParam.filter(
        (modbusParameter: any) => modbusParameter.isRegisterEnabled
      ).length
      : 0

  return (
    <div className="modbus-parameters">
      <Grid container>
        <Grid item xs={12}>
          <HContainer>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Heading5 data-testid="standard-text">
                {AddDeviceStrings.OtherParameters}
              </Heading5>
              <Span>
                {selectedModbusCount} {AddDeviceStrings.Selected}
              </Span>
            </div>
          </HContainer>
          <BContainer>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: '0px 20px 12px'
              }}
            >
              <Input
                type="search"
                placeholder={AddDeviceStrings.SearchParameters}
                onChange={handleSearchInputChange}
                value={searchText}
                data-testid="search-input"
              />
              <Button
                className="save-default-btn"
                style={{
                  fontFamily: 'Honeywell Sans Web',
                  textTransform: 'none',
                  padding: '8px 20px',
                  height: '35px'
                }}
                size="small"
                data-testid="save-params"
                variant="outlined"
                disabled={defaultViewSaved || !hasRegisterEnabledParameter}
                onClick={saveDefaultParamConfig}
              >
                {AddDeviceStrings.SaveAsDefaultView}
              </Button>
            </div>
            <ParametersTable
              tableHeaders={tableHeaders}
              parameters={props.parameters.length > 0 ? props.parameters : []}
              searchText={searchText}
              selectedModbusCount={selectedModbusCount}
              toggleSelectAll={toggleSelectAll}
              toggleSelectIndicator={toggleSelectIndicator}
              handleChangeTextBox={handleChangeTextBox}
              handleAlarmSelect={handleAlarmSelect}
              handleCancel={handleCancel}
            />
          </BContainer>
          {isDefaultParamSaved && handleDefaultParamWithFailure()}
        </Grid>
      </Grid>
    </div>
  )
}

interface DispatchToProps {
  saveDefaultParamConfig: (
    defaultParamRequest: any,
    assetTypeId: string
  ) => void
}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  saveDefaultParamConfig: (defaultParamRequest: any, assetTypeId: string) =>
    dispatch(saveDefaultParamConfig(defaultParamRequest, assetTypeId))
})

interface StateToProps {
  defaultParameterState: DefaultParameterState
}
const mapStateToProps = (state: RootState): StateToProps => ({
  defaultParameterState: state.defaultParameterState
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

export default connector(ModbusParameters)
